import React from "react";
import { useNavigate } from "react-router-dom";

function BelowRightCard({ profileData }) {
  const navigate = useNavigate();
  // Provide safe default values if profileData is empty or undefined
  const profileURL = profileData?.profileURL ?? "/Assets/default_user.png";

  // Convert "profileCompleteness" to a number; default to 0 if not provided
  const completenessNumber = parseFloat(profileData?.profileCompleteness) || 0;

  // If "completeness" is missing, set each section to "0" to force a red cross
  const completeness = profileData?.completeness ?? {
    profilePicture: "0",
    personalDetails: "0",
    contactDetails: "0",
    addressDetails: "0",
    educationDetails: "0",
  };

  // Decide if a field is "completed" (100.00)
  const isCompleted = (value) => parseFloat(value) === 100;

  // Render a check or cross icon based on completion
  const renderIcon = (sectionValue) => {
    return isCompleted(sectionValue) ? (
      <span className="text-green-500">
        {/* Green check icon (Heroicons Solid) */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="currentColor"
          viewBox="0 0 20 20"
          className="h-5 w-5"
        >
          <path
            fillRule="evenodd"
            d="M16.707 5.293a1 1 0 010 1.414l-7.999 8a1 1 0 01-1.414 
            0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 
            011.414 0z"
            clipRule="evenodd"
          />
        </svg>
      </span>
    ) : (
      <span className="text-red-500">
        {/* Red cross icon (Heroicons Solid) */}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5"
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fillRule="evenodd"
            d="M5.293 4.293a1 1 0 011.414 0L10 7.586l3.293-3.293a1 
            1 0 111.414 1.414L11.414 9l3.293 3.293a1 1 0 
            11-1.414 1.414L10 10.414l-3.293 3.293a1 1 0 
            11-1.414-1.414L8.586 9 5.293 5.707a1 1 0 
            010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </span>
    );
  };

  return (
    <div className="p-6 bg-white rounded shadow-md">
      {/* Heading */}
      <h2 className="text-lg font-bold text-gray-500 mb-4">
        Profile Completeness
      </h2>

      {/* User Image */}
      <div className="flex justify-center mb-4">
        <img
          src={profileURL}
          alt="User Profile"
          className="w-32 h-32 rounded-full border border-gray-300"
        />
      </div>

      {/* Overall Progress Bar */}
      <div className="relative pt-1 mb-3">
        <div className="flex mb-2 items-center justify-between">
          <div>
            <span className="text-xs font-semibold inline-block py-1 uppercase" />
          </div>
          <div className="text-right">
            <span className="text-xs font-semibold inline-block py-1">
              {completenessNumber}% Completed
            </span>
          </div>
        </div>

        <div className="w-full bg-gray-200 rounded-full h-6">
          <div
            className="bg-green-500 h-6 rounded-full"
            style={{ width: `${completenessNumber}%` }}
          />
        </div>
      </div>

      {/* Profile Picture */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-gray-700">Profile Picture</span>
        {renderIcon(completeness.profilePicture)}
      </div>

      <hr className="my-2" />

      {/* Personal Details */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-gray-700">Personal Details</span>
        {renderIcon(completeness.personalDetails)}
      </div>

      <hr className="my-2" />

      {/* Contact Details */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-gray-700">Contact Details</span>
        {renderIcon(completeness.contactDetails)}
      </div>

      <hr className="my-2" />

      {/* Address Details */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-gray-700">Address Details</span>
        {renderIcon(completeness.addressDetails)}
      </div>

      <hr className="my-2" />

      {/* Education Details */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-gray-700">Education Details</span>
        {renderIcon(completeness.educationDetails)}
      </div>

      <hr className="my-2" />

      {/* Update Profile Button (Optional) */}
      <div className="flex justify-end">
        <button 
          onClick={() => navigate("/profile")}
          className="flex items-center text-blue-500 py-2 px-4 rounded-full mt-4 hover:bg-blue-50 transition-colors">
          <span className="font-bold">Update Profile</span>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="3"
              d="M13 5l7 7-7 7"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}

export default BelowRightCard;
